// Settings & Integrations (PRD §4.9 + §4.11). Tabbed: tenant profile, quiet
// hours, API keys, and the Integrations Hub card grid. Mock-only (no saves).
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import Toggle from '../components/ui/Toggle';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  useTenantProfile,
  updateTenantProfile,
  useQuietHours,
  updateQuietHours,
  type TenantProfile,
  type QuietHours,
} from '../lib/settings';
import { useApiKeys, createApiKey, revokeApiKey } from '../lib/apiKeys';
import {
  useWebhookConfig, saveWebhook, rotateWebhookSecret, testWebhook,
  WEBHOOK_EVENTS, DATA_FIELDS, type WebhookEvent, type DataField,
} from '../lib/webhooks';
import { apiBaseUrl } from '../lib/firebase';

type Tab = 'general' | 'quiet' | 'api' | 'integrations';
const TABS: Tab[] = ['general', 'quiet', 'api', 'integrations'];

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold text-ink-2">{label}</span>
      {children}
    </label>
  );
}

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';

function GeneralTab() {
  const { t } = useI18n();
  const { profile, loading } = useTenantProfile();
  const [form, setForm] = useState<TenantProfile | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (profile) setForm(profile);
  }, [profile]);

  if (loading || !form) {
    return (
      <Card>
        <div className="py-6 text-sm text-ink-3">…</div>
      </Card>
    );
  }

  async function save() {
    if (!form) return;
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      await updateTenantProfile(form);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('set.gen.business')}>
          <input
            value={form.businessName}
            onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label={t('set.gen.timezone')}>
          <input
            value={form.timezone}
            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            className={inputCls}
          />
        </Field>
        <Field label={t('set.gen.language')}>
          <select
            value={form.defaultLang}
            onChange={(e) => setForm({ ...form, defaultLang: e.target.value as 'en' | 'ar' })}
            className={cn(inputCls, 'bg-white')}
          >
            <option value="en">{t('set.lang.en')}</option>
            <option value="ar">{t('set.lang.ar')}</option>
          </select>
        </Field>
      </div>
      {err && <div className="mt-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
      <button
        type="button"
        onClick={save}
        disabled={saving || !form.businessName.trim()}
        className="mt-4 rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40"
      >
        {saving ? t('set.gen.saving') : saved ? t('set.gen.saved') : t('set.gen.save')}
      </button>
    </Card>
  );
}

function QuietTab() {
  const { t } = useI18n();
  const { quiet, loading } = useQuietHours();
  const [q, setQ] = useState<QuietHours | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (quiet) setQ(quiet);
  }, [quiet]);

  if (loading || !q) {
    return (
      <Card>
        <div className="py-6 text-sm text-ink-3">…</div>
      </Card>
    );
  }

  async function save(next: QuietHours) {
    setQ(next);
    setSaving(true);
    setErr(null);
    setSaved(false);
    try {
      await updateQuietHours(next);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <b className="block text-sm text-ink">{t('set.quiet.enable')}</b>
          <span className="text-xs text-ink-3">{t('set.quiet.note')}</span>
        </div>
        <Toggle on={q.enabled} onChange={(v) => void save({ ...q, enabled: v })} label={t('set.quiet.enable')} />
      </div>
      <div className={cn('mt-4 space-y-4', !q.enabled && 'pointer-events-none opacity-40')}>
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label={t('set.quiet.start')}>
            <input type="time" value={q.start} onChange={(e) => setQ({ ...q, start: e.target.value })} onBlur={() => void save(q)} className={inputCls} />
          </Field>
          <Field label={t('set.quiet.end')}>
            <input type="time" value={q.end} onChange={(e) => setQ({ ...q, end: e.target.value })} onBlur={() => void save(q)} className={inputCls} />
          </Field>
          <Field label={t('set.quiet.offset')}>
            <input type="number" min={-12} max={14} value={q.utcOffset} onChange={(e) => setQ({ ...q, utcOffset: Number(e.target.value) })} onBlur={() => void save(q)} className={inputCls} />
          </Field>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-ink">{t('set.quiet.friday')}</span>
          <Toggle on={q.fridayPrayer} onChange={(v) => void save({ ...q, fridayPrayer: v })} label={t('set.quiet.friday')} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-ink">{t('set.quiet.ramadan')}</span>
          <Toggle on={q.ramadan} onChange={(v) => void save({ ...q, ramadan: v })} label={t('set.quiet.ramadan')} />
        </div>
      </div>
      {err && <div className="mt-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
      {(saving || saved) && (
        <div className="mt-4 text-xs font-semibold text-ink-3">{saving ? t('set.gen.saving') : t('set.gen.saved')}</div>
      )}
    </Card>
  );
}

function fmtDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
}

function ApiTab() {
  const { t } = useI18n();
  const confirm = useConfirm();
  const { keys, loading } = useApiKeys();
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<{ key: string; label: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const activeKeys = keys.filter((k) => !k.revoked);

  async function create() {
    if (!label.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await createApiKey(label.trim());
      setFresh({ key: res.key, label: res.label });
      setLabel('');
      setCreating(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copy(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked — user can select manually */ }
  }

  async function revoke(id: string) {
    if (!(await confirm({ title: t('set.api.revoke'), body: t('set.api.revokeConfirm'), confirmLabel: t('set.api.revoke'), danger: true }))) return;
    try {
      await revokeApiKey(id);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <b className="block text-sm text-ink">{t('set.api.base')}</b>
        <p className="mt-1 text-xs text-ink-3">{t('set.api.baseNote')}</p>
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 overflow-x-auto rounded-[9px] bg-surface px-3 py-2 font-mono text-xs text-ink-2">{apiBaseUrl}</code>
          <button type="button" onClick={() => void copy(apiBaseUrl)} className="rounded-[9px] border border-slate-200 px-3 py-2 text-xs font-bold text-ink">
            {copied ? t('set.api.copied') : t('set.api.copy')}
          </button>
        </div>
      </Card>

      {fresh && (
        <div className="rounded-card border border-primary bg-primary-50 p-4">
          <b className="block text-sm text-primary">{t('set.api.newKey')}</b>
          <p className="mt-1 text-xs text-ink-2">{t('set.api.newKeyNote')}</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 overflow-x-auto rounded-[9px] bg-white px-3 py-2 font-mono text-xs text-ink">{fresh.key}</code>
            <button type="button" onClick={() => void copy(fresh.key)} className="rounded-[9px] bg-primary px-3 py-2 text-xs font-bold text-white">
              {copied ? t('set.api.copied') : t('set.api.copy')}
            </button>
          </div>
          <button type="button" onClick={() => setFresh(null)} className="mt-3 text-xs font-bold text-ink-3 hover:text-ink">
            {t('set.api.dismiss')}
          </button>
        </div>
      )}

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <b className="text-sm text-ink">{t('set.api.title')}</b>
          {!creating && (
            <button type="button" onClick={() => setCreating(true)} className="rounded-[9px] bg-primary px-3 py-1.5 text-xs font-bold text-white">
              + {t('set.api.create')}
            </button>
          )}
        </div>

        {creating && (
          <div className="mb-4 flex flex-wrap items-end gap-2 rounded-[9px] bg-surface p-3">
            <Field label={t('set.api.col.label')}>
              <input
                autoFocus
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void create()}
                placeholder={t('set.api.labelPlaceholder')}
                className={inputCls}
              />
            </Field>
            <button type="button" onClick={() => void create()} disabled={busy || !label.trim()} className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">
              {busy ? '…' : t('set.api.generate')}
            </button>
            <button type="button" onClick={() => { setCreating(false); setLabel(''); }} className="rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink">
              {t('set.api.cancel')}
            </button>
          </div>
        )}

        {err && <div className="mb-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}

        {loading ? (
          <div className="py-6 text-sm text-ink-3">…</div>
        ) : activeKeys.length === 0 ? (
          <p className="py-6 text-center text-sm text-ink-3">{t('set.api.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[500px] border-collapse">
              <thead>
                <tr>
                  {['set.api.col.label', 'set.api.col.key', 'set.api.col.created', 'set.api.col.used', 'perm.col.actions'].map((k) => (
                    <th key={k} className="whitespace-nowrap bg-surface px-3 py-2 text-start text-[11px] font-bold uppercase tracking-wide text-ink-3">
                      {t(k)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeKeys.map((k) => (
                  <tr key={k.id} className="border-t border-slate-200">
                    <td className="px-3 py-2.5 text-[13px] font-semibold text-ink">{k.label}</td>
                    <td className="px-3 py-2.5 font-mono text-xs text-ink-2">{k.prefix}••••{k.last4}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-ink-3">{fmtDate(k.createdAt)}</td>
                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-ink-3">{k.lastUsedAt ? fmtDate(k.lastUsedAt) : t('set.api.never')}</td>
                    <td className="px-3 py-2.5">
                      <button type="button" onClick={() => void revoke(k.id)} className="rounded-[8px] border border-red-500 bg-white px-3 py-1 text-xs font-bold text-red-500">
                        {t('set.api.revoke')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-ink-3">{t('set.api.note')}</p>
      </Card>
    </div>
  );
}


function WebhookCard() {
  const { t } = useI18n();
  const { config, loading } = useWebhookConfig();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [dataFields, setDataFields] = useState<DataField[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [busy, setBusy] = useState<'save' | 'test' | 'rotate' | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !hydrated) {
      setUrl(config.url);
      setEvents(config.events.length ? config.events : [...WEBHOOK_EVENTS]);
      setDataFields(config.dataFields?.length ? config.dataFields : [...DATA_FIELDS]);
      setEnabled(config.enabled);
      setHydrated(true);
    }
  }, [loading, hydrated, config]);

  function toggleEvent(e: WebhookEvent) {
    setEvents((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
  }

  function toggleField(f: DataField) {
    setDataFields((cur) => (cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]));
  }

  async function save() {
    if (!url.trim() || !events.length) return;
    setBusy('save'); setErr(null); setTestMsg(null);
    try {
      const res = await saveWebhook(url.trim(), events, enabled, dataFields);
      if (res.secret) setSecret(res.secret);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  async function rotate() {
    setBusy('rotate'); setErr(null);
    try { const res = await rotateWebhookSecret(); setSecret(res.secret); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  async function test() {
    setBusy('test'); setErr(null); setTestMsg(null);
    try {
      const r = await testWebhook();
      setTestMsg(r.ok ? t('set.wh.testOk') : `${t('set.wh.testFail')}${r.status ? ` (${r.status})` : r.error ? ` — ${r.error}` : ''}`);
    } catch (e) { setErr((e as Error).message); } finally { setBusy(null); }
  }

  return (
    <Card>
      <div className="flex items-start gap-3">
        <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-[12px] bg-surface text-xl" aria-hidden>📡</span>
        <div className="min-w-0 flex-1">
          <b className="block text-sm text-ink">{t('set.wh.title')}</b>
          <p className="text-xs text-ink-3">{t('set.wh.note')}</p>
        </div>
        <Toggle on={enabled} onChange={setEnabled} label={t('set.wh.enable')} />
      </div>

      <div className="mt-4 space-y-4">
        <Field label={t('set.wh.url')}>
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.example.com/webhook" aria-label={t('set.wh.url')} className={inputCls} />
        </Field>

        <div>
          <span className="mb-1 block text-xs font-bold text-ink-2">{t('set.wh.events')}</span>
          <div className="grid gap-2 sm:grid-cols-2">
            {WEBHOOK_EVENTS.map((e) => (
              <label key={e} className="flex items-center gap-2 rounded-[9px] border border-slate-200 px-3 py-2 text-[13px] text-ink">
                <input type="checkbox" checked={events.includes(e)} onChange={() => toggleEvent(e)} className="accent-primary" />
                <code className="text-xs">{e}</code>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="block text-xs font-bold text-ink-2">{t('set.wh.dataFields')}</span>
            <div className="flex gap-2 text-xs">
              <button type="button" onClick={() => setDataFields([...DATA_FIELDS])} className="text-primary hover:underline">{t('common.selectAll')}</button>
              <button type="button" onClick={() => setDataFields([])} className="text-slate-400 hover:underline">{t('common.clear')}</button>
            </div>
          </div>
          <p className="text-[11px] text-ink-3 mb-2">{t('set.wh.dataFieldsNote')}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {DATA_FIELDS.map((f) => (
              <label key={f} className="flex items-center gap-2 rounded-[9px] border border-slate-200 px-3 py-2 text-[12px] text-ink">
                <input type="checkbox" checked={dataFields.includes(f)} onChange={() => toggleField(f)} className="accent-primary" />
                <code className="text-[11px]">{f}</code>
              </label>
            ))}
          </div>
        </div>

        {secret && (
          <div className="rounded-[9px] border border-primary bg-primary-50 p-3">
            <b className="block text-xs text-primary">{t('set.wh.secret')}</b>
            <p className="mt-0.5 text-[11px] text-ink-2">{t('set.wh.secretNote')}</p>
            <code className="mt-1 block overflow-x-auto rounded-[8px] bg-white px-2 py-1.5 font-mono text-xs text-ink">{secret}</code>
            <button type="button" onClick={() => setSecret(null)} className="mt-2 text-[11px] font-bold text-ink-3 hover:text-ink">{t('set.api.dismiss')}</button>
          </div>
        )}

        {err && <div className="rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
        {testMsg && <div className="rounded-[9px] bg-surface px-3 py-2 text-xs font-semibold text-ink-2">{testMsg}</div>}
        {config.lastDeliveryAt && (
          <div className="text-[11px] text-ink-3">
            {t('set.wh.last')}: {config.lastOk ? '✓' : '✕'} {config.lastStatus ?? config.lastError ?? ''} · {fmtDate(config.lastDeliveryAt)}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => void save()} disabled={busy !== null || !url.trim() || !events.length}
            className="rounded-[9px] bg-primary px-4 py-2 text-[13px] font-bold text-white disabled:opacity-40">
            {busy === 'save' ? '…' : t('set.gen.save')}
          </button>
          <button type="button" onClick={() => void test()} disabled={busy !== null || !config.url}
            className="rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink disabled:opacity-40">
            {busy === 'test' ? '…' : t('set.wh.test')}
          </button>
          {config.hasSecret && (
            <button type="button" onClick={() => void rotate()} disabled={busy !== null}
              className="rounded-[9px] border border-slate-200 px-4 py-2 text-[13px] font-bold text-ink disabled:opacity-40">
              {busy === 'rotate' ? '…' : t('set.wh.rotate')}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function IntegrationsTab({ onOpenApi: _onOpenApi }: { onOpenApi: () => void }) {
  const { t } = useI18n();

  return (
    <div className="space-y-5">
      <WebhookCard />
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <b className="block text-base text-ink">{t('integrations.hubCardTitle')}</b>
            <p className="mt-1 text-xs text-ink-3 max-w-xl">
              {t('integrations.hubCardDesc')}
            </p>
          </div>
          <Link
            to="/integrations"
            className="inline-flex items-center justify-center rounded-[9px] bg-primary px-4 py-2 text-xs font-bold text-white shrink-0 hover:bg-primary/90 transition"
          >
            {t('integrations.openHub')} <span aria-hidden="true" className="rtl:rotate-180 inline-block">→</span>
          </Link>
        </div>
      </Card>
    </div>
  );
}

// Deep links from other pages (Bot Manager → "Outbound Webhooks") name the
// section, not our internal tab id. Map them here; unknown values are ignored.
const TAB_ALIASES: Record<string, Tab> = { webhooks: 'api', apikeys: 'api', 'api-keys': 'api' };

function tabFromSearch(search: string, fallback: Tab): Tab {
  const raw = new URLSearchParams(search).get('tab');
  if (!raw) return fallback;
  if ((TABS as string[]).includes(raw)) return raw as Tab;
  return TAB_ALIASES[raw] ?? fallback;
}

export default function Settings({ initialTab = 'general' }: { initialTab?: Tab }) {
  const { t } = useI18n();
  const location = useLocation();
  const [tab, setTab] = useState<Tab>(() => tabFromSearch(location.search, initialTab));

  // Honour ?tab= when the URL changes while mounted (e.g. sidebar → card link).
  useEffect(() => {
    const next = tabFromSearch(location.search, tab);
    if (next !== tab) setTab(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <div>
      <h1 className="text-[22px] font-extrabold text-ink">{t('set.title')}</h1>
      <p className="mb-4 text-ink-3">{t('set.subtitle')}</p>

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map((tb) => (
          <button
            key={tb}
            type="button"
            onClick={() => setTab(tb)}
            className={cn(
              '-mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-bold',
              tab === tb ? 'border-primary text-primary' : 'border-transparent text-ink-3 hover:text-ink',
            )}
          >
            {t(`set.tab.${tb}`)}
          </button>
        ))}
      </div>

      {tab === 'general' && <GeneralTab />}
      {tab === 'quiet' && <QuietTab />}
      {tab === 'api' && <ApiTab />}
      {tab === 'integrations' && <IntegrationsTab onOpenApi={() => setTab('api')} />}
    </div>
  );
}
